import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { ApolloError } from '@apollo/react-hooks';
import { useState } from 'react';
import { AxiosCreateRequestProps, ServiceRequestResponse } from 'serviceRequest/types/serviceRequest';
import useQueryErrorHandler from 'core/hooks/useQueryErrorHandler';
import { mutationCreateServiceRequest } from './gql/mutationCreateServiceRequest';
import { mutationCreateServiceRequestV3 } from './gql/mutationCreateServiceRequestV3';
import TokenService from 'core/config/gql/TokenService';
import SessionService from 'core/config/gql/SessionService';

const useAxiosMakeServiceRequest = () => {
    const tokenInstance = TokenService.getInstance();
    const sessionService = SessionService.getInstance();

    const formData = new FormData();
    const { errorHandler } = useQueryErrorHandler();

    const baseUrl: string = process.env.REACT_APP_GQL_SERVER_URL as string;

    const [axiosData, setAxiosData] = useState<ServiceRequestResponse | null>(null);
    const [axiosError, setAxiosError] = useState<AxiosError>();
    const [axiosLoading, setAxiosLoading] = useState(false);
    const [uploading, setUploading] = useState(0);

    const createRequest = async ({ files, serviceData }: AxiosCreateRequestProps) => {
        if (tokenInstance.shouldRefreshToken()) {
            await tokenInstance.refreshToken().catch((error) => {
                console.error(`[Token refresh error]: ${error}`);
                sessionService.logoff();
            });
        }

        const token = tokenInstance.getToken();

        setAxiosLoading(true);
        setAxiosError(() => undefined);

        const filesMap: { [key: string]: [string] } = {};
        const filesKeys = Object.keys(files);
        const dummyFilesArray: null[] = [];
        filesKeys.forEach((key) => {
            dummyFilesArray.push(null);
            filesMap[key] = ['variables.serviceData.files.' + key];
            formData.append(key, files[+key]);
        });
        const { rentalObjectId, rentalObjectUuid, ...sharedServiceData } = serviceData;
        const useObjectUuid = Boolean(rentalObjectUuid);
        const queryBody = (useObjectUuid ? mutationCreateServiceRequestV3 : mutationCreateServiceRequest).loc?.source
            .body;
        const operations = {
            query: queryBody,
            variables: {
                serviceData: {
                    ...sharedServiceData,
                    ...(useObjectUuid ? { rentalObjectUuid } : { rentalObjectId }),
                    files: dummyFilesArray,
                },
            },
        };
        formData.append('operations', JSON.stringify(operations));
        formData.append('map', JSON.stringify(filesMap));

        const config: AxiosRequestConfig = {
            headers: {
                'X-Auth-Token': `${token}`,
                Accept: 'application/json',
                'Content-Type': 'multipart/form-data',
            },
            method: 'post',
            baseURL: baseUrl,
            data: formData,
            onUploadProgress: (progressEvent) => {
                const progressPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploading(() => progressPercent);
            },
        };

        try {
            const response = await axios(config);
            if ('errors' in response.data) setAxiosError(response.data.errors[0]);

            setAxiosData(() => response.data.data);
        } catch (error) {
            console.error('error', error);

            setAxiosError(error as AxiosError);
            errorHandler(error as ApolloError);
        } finally {
            setAxiosLoading(false);
        }
    };

    return { axiosError, axiosLoading, axiosData, createRequest, uploading };
};

export default useAxiosMakeServiceRequest;
